import { marked } from 'marked';
import { Options as ReactMarkdownProps } from 'react-markdown';

interface ParserResult {
  type: 'simple' | 'sectioned';
  content: Content[];
}

export interface Content {
  heading?: string;
  body: string;
}

interface SectionToken {
  type: string;
  raw: string;
  depth: number;
  text: string;
  tokens: marked.Token[];
}

/**
 * See: https://marked.js.org/using_pro#extensions
 */
const SectionTokenizerExtension: marked.TokenizerExtension = {
  level: 'block',
  name: 'SectionTokenizerExtensison',
  tokenizer(this, src) {
    // Previously used their lheading rule directly, but was changed in 4.2.3. Now hardcoding the old one.
    // Taken from: https://github.com/markedjs/marked/pull/2655/files#diff-1de7312ecbb8138fea99c0f206d07d4e534c7a513f615d3a54cc66bc9c702c67L30
    const rule = /^([^\n]+)\n {0,3}(=+|-+) *(?:\n+|$)/;
    const match = rule.exec(src);
    if (!match) {
      return;
    }

    const [raw, text, headingChars] = match;
    // Headings considered sections of the accordion need to have a "=" char
    if (headingChars.charAt(0) === '=') {
      const token = {
        raw,
        text,
        type: 'section',
        depth: 1,
        tokens: [],
      };
      this.lexer.inline(token.text, token.tokens);
      return token;
    }
  },
};

marked.use({ extensions: [SectionTokenizerExtension] });

/**
 * parseAssignmentMarkdown implements the parsing logic for the markdown that's passed into it.
 * @param markdown parsed to capture section tokens.
 */
export function parseMarkdown(markdown: string): ParserResult {
  const tokens = marked.lexer(markdown.trimStart()) as (marked.Token | SectionToken)[];
  const isSectioned = tokens.some(token => token.type === 'section');

  if (isSectioned) {
    return {
      type: 'sectioned',
      content: tokens.reduce<Content[]>((content, token) => {
        if (token.type === 'section') {
          content.push({
            heading: token.text,
            body: '',
          });
        } else {
          if (!content.length) {
            content.push({
              heading: 'Introduction',
              body: '',
            });
          }

          content[content.length - 1].body += token.raw + '\n';
        }

        return content;
      }, []),
    };
  }

  return {
    type: 'simple',
    content: [{ body: tokens.map(token => token.raw + '\n').join('') }],
  };
}

export const defaultComponents: ReactMarkdownProps['components'] = {
  a: ({ node, ...props }) => {
    const href = props.href ?? '';

    // By default we want to open external links in a new tab
    if (href.startsWith('http')) {
      props.rel ??= 'noopener noreferrer';
      props.target ??= '_blank';
    }

    // If the link is a tab link
    if (/^tab-([0-9]*)$/.test(href)) {
      props.href = `#${href}`;
    }

    const customButtonProps = getCustomButtonProps(props.children);
    if (customButtonProps) {
      props.className = customButtonProps.buttonClass;
      props.style = customButtonProps.buttonStyles;
      props.children = [customButtonProps.attrs.label];
    }

    // Some clients (e.g. datadog) pass classes & id to their anchors for custom functionality
    return <a {...props}>{props.children}</a>;
  },
};

function getCustomButtonProps(children: React.ReactNode) {
  const text = Array.isArray(children) ? children.join('') : (children as string);
  const buttonStylePattern = /button\b(?<attributes>.*)/;
  const matches = buttonStylePattern.exec(text);

  if (!matches?.length) {
    return null;
  }

  const attrs = Object.fromEntries(
    Array.from(matches[1].matchAll(/(\w+)(?:="(.*?)")?/g), match => [match[1], match[2] || true]),
  );

  let buttonClass = 'px-1 py-half mx-half rounded !no-underline';
  const buttonStyles: React.CSSProperties = {};

  if (typeof attrs.variant === 'string') {
    if (attrs.variant === 'white' || (!attrs.variant && !attrs.background)) {
      buttonClass += ' !text-primary bg-white';
    } else if (attrs.variant === 'danger') {
      buttonClass += ' !text-white bg-red';
    } else if (attrs.variant === 'warning') {
      buttonClass += ' !text-[#000] bg-warning';
    } else if (attrs.variant === 'success') {
      buttonClass += ' !text-white bg-green';
    } else if (attrs.variant === 'outline') {
      buttonClass += ' !text-white border border-white';
    }
  } else if (!attrs.variant && !attrs.background) {
    buttonClass += ' !text-primary bg-white';
  } else if (attrs.background || attrs.color) {
    if (!attrs.color) {
      buttonClass += ` !text-white`;
    }
    buttonStyles.backgroundColor = typeof attrs.background === 'string' ? attrs.background : '';
    buttonStyles.color = typeof attrs.color === 'string' ? attrs.color : '';
  }

  if (attrs.block === true) {
    buttonClass += ' block w-full text-center';
  }

  return { buttonClass, buttonStyles, attrs };
}
